import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent, EditorContext } from '@tiptap/react';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import {
  createRichTextEditorStarterKit,
  createBugherdLinkExtension,
  BugherdMentionWithReactView
} from '../RichTextDescription/extensions/tiptapShared';
import { createCodeBlockExtension } from '../RichTextDescription/extensions/codeBlockExtension';
import { SlashCommand } from '../RichTextDescription/extensions/slashCommand';
import { createMarkdownPasteHandler } from '../RichTextDescription/extensions/markdownPaste';
import {
  GlobalEditorShortcuts,
  TOGGLE_MARKDOWN_SOURCE_EVENT,
  isMarkdownToggleShortcut,
  onEditorEvent,
  offEditorEvent
} from '../RichTextDescription/extensions/globalShortcuts';
import { useSlashCommandConfig } from '../RichTextDescription/useSlashCommand';
import { commentComposerToolbarDensity } from '../RichTextDescription/utils/toolbarLayout';
import { CommentMentionSuggestion } from './extensions/commentMentionSuggestion';
import { useCommentMentionSuggestion } from './useCommentMentionSuggestion';
import { Toolbar } from '../RichTextDescription/components/Toolbar';
import { RichTextBubbleMenu } from '../RichTextDescription/components/RichTextBubbleMenu';
import { markdownToHtml, htmlToMarkdown } from '../RichTextDescription/utils/serialization';
import { MentionPreviewContext } from '../RichTextDescription/components/mentionPreviewContext';
import { resolveMentionPreviewContext } from '../RichTextDescription/types';
import type { MarkdownMentionPreviewContext, ShortcutConfig, User } from '../RichTextDescription/types';
import styles from './index.module.css';
import proseStyles from '../RichTextDescription/richTextProse.module.css';

export const COMMENT_CHARACTER_LIMIT = 2000;
export const COMMENT_COMPOSER_COLLAPSE_MS = 220;
const COLLAPSED_EDITOR_BOX_HEIGHT_PX = 62;

export type RichTextCommentProps = {
  value: string;
  onChange: (value: string) => void;
  mentionableUsers?: User[];
  placeholder?: string;
  submitTask?: () => void;
  inputDataCy?: string;
  autoFocus?: boolean;
  mentionPreviewContext?: MarkdownMentionPreviewContext;
  isPrivate?: boolean;
  isCollapsing?: boolean;
  canTogglePrivateComment?: boolean;
  canCreatePrivateComments?: boolean;
  isPrivateComment?: boolean;
  hasInvalidMentions?: boolean;
  onPrivateCommentToggle?: (checked: boolean) => void;
  onPrivateCommentUpgrade?: () => void;
};

const NOOP_SHORTCUT_CONFIG_BASE = {
  assignees: [] as number[],
  canEditAssignees: false,
  assignableUsers: [] as User[],
  onAssigneesChange: () => {},
  canEditStatus: false,
  onStatusChange: () => {},
  columns: [],
  tags: [],
  canEditTags: false,
  onTagsChange: () => {},
  availableTags: [],
  severity: undefined,
  canEditSeverity: false,
  onSeverityChange: () => {}
};

export const RichTextComment = ({
  value,
  onChange,
  mentionableUsers = [],
  placeholder = 'Add a comment...',
  submitTask,
  inputDataCy,
  autoFocus = false,
  mentionPreviewContext,
  isPrivate = false,
  isCollapsing = false,
  canTogglePrivateComment = false,
  canCreatePrivateComments = false,
  isPrivateComment = false,
  hasInvalidMentions = false,
  onPrivateCommentToggle,
  onPrivateCommentUpgrade
}: RichTextCommentProps) => {
  const editorDataCy = inputDataCy ?? 'rich-text-comment';
  const popupContainerRef = useRef<HTMLDivElement>(null);
  const editorBoxRef = useRef<HTMLDivElement>(null);
  const markdownTextareaRef = useRef<HTMLTextAreaElement>(null);
  const prevShowMarkdownTextareaRef = useRef(false);
  const showMarkdownTextareaRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const [collapseVisual, setCollapseVisual] = useState(false);
  const [markdownEditPreferred, setMarkdownEditPreferred] = useState(false);
  const [markdownDraft, setMarkdownDraft] = useState<string | null>(null);
  const [composerWidth, setComposerWidth] = useState(0);

  const mentionConfig = useCommentMentionSuggestion(mentionableUsers, popupContainerRef);

  const shortcutConfig: ShortcutConfig = useMemo(
    () => ({
      ...NOOP_SHORTCUT_CONFIG_BASE,
      mentionableTasks: mentionPreviewContext?.mentionableTasks ?? [],
      commentSlash: {
        mentionableUsers,
        canTogglePrivate: canTogglePrivateComment && !!onPrivateCommentToggle,
        canCreatePrivate: canCreatePrivateComments,
        isPrivate: isPrivateComment,
        hasInvalidMentions,
        onPrivateToggle: onPrivateCommentToggle ?? (() => {}),
        onPrivateUpgrade: onPrivateCommentUpgrade
      }
    }),
    [
      mentionPreviewContext?.mentionableTasks,
      mentionableUsers,
      canTogglePrivateComment,
      canCreatePrivateComments,
      isPrivateComment,
      hasInvalidMentions,
      onPrivateCommentToggle,
      onPrivateCommentUpgrade
    ]
  );
  const slashConfig = useSlashCommandConfig(shortcutConfig, popupContainerRef);

  const extensions = useMemo(
    () => [
      createRichTextEditorStarterKit(),
      createCodeBlockExtension(),
      Placeholder.configure({ placeholder }),
      Underline,
      Highlight,
      createBugherdLinkExtension({ openOnClick: true }),
      GlobalEditorShortcuts,
      Image,
      TaskList,
      TaskItem.configure({ nested: true }),
      BugherdMentionWithReactView,
      CommentMentionSuggestion.configure({
        items: mentionConfig.items,
        render: mentionConfig.render,
        hasMentionableUsers: mentionConfig.hasMentionableUsers
      }),
      SlashCommand.configure({
        items: slashConfig.items,
        render: slashConfig.render
      })
    ],
    [
      placeholder,
      mentionConfig.items,
      mentionConfig.render,
      mentionConfig.hasMentionableUsers,
      slashConfig.items,
      slashConfig.render
    ]
  );

  const editor = useEditor({
    extensions,
    content: markdownToHtml(value),
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        class: `${styles.editor} ${proseStyles.proseRoot}`,
        'data-cy': editorDataCy
      },
      handleKeyDown: (_view, event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && submitTask) {
          event.preventDefault();
          submitTask();
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        paste: createMarkdownPasteHandler(showMarkdownTextareaRef)
      }
    },
    immediatelyRender: false
  });

  const lastEmittedValueRef = useRef<string | symbol>(Symbol('initial'));

  useEffect(() => {
    if (!editor) return;
    if (value === lastEmittedValueRef.current) return;
    lastEmittedValueRef.current = value;
    editor.commands.setContent(markdownToHtml(value), false);
  }, [value, editor]);

  useEffect((): (() => void) | void => {
    if (!editor) return;
    const syncMarkdown = () => {
      const str = htmlToMarkdown(editor.getHTML());
      if (str.length <= COMMENT_CHARACTER_LIMIT) {
        lastEmittedValueRef.current = str;
        onChange(str);
      }
    };
    editor.on('update', syncMarkdown);
    return (): void => {
      editor.off('update', syncMarkdown);
    };
  }, [editor, onChange]);

  useLayoutEffect(() => {
    const box = editorBoxRef.current;
    if (!isCollapsing) {
      setCollapseVisual(false);
      if (box) {
        box.style.height = '';
        box.style.overflow = '';
        box.style.transition = '';
      }
      return;
    }

    if (!box || collapseVisual) return;

    const from = box.getBoundingClientRect().height;
    box.style.overflow = 'hidden';
    box.style.transition = 'none';
    box.style.height = `${from}px`;
    setCollapseVisual(true);
  }, [isCollapsing, collapseVisual]);

  useEffect(() => {
    if (!isCollapsing || !collapseVisual) return;
    const box = editorBoxRef.current;
    if (!box) return;

    const frame = window.requestAnimationFrame(() => {
      box.style.transition = `height ${COMMENT_COMPOSER_COLLAPSE_MS}ms ease`;
      box.style.height = `${COLLAPSED_EDITOR_BOX_HEIGHT_PX}px`;
      editor?.commands.blur();
      setIsFocused(false);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isCollapsing, collapseVisual, editor]);

  const showMarkdownTextarea = isFocused && markdownEditPreferred;
  showMarkdownTextareaRef.current = showMarkdownTextarea;

  const handleContainerFocus = useCallback(() => {
    if (isCollapsing) return;
    setIsFocused(true);
  }, [isCollapsing]);

  const handleContainerBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const next = e.relatedTarget as Node | null;
      if (next && e.currentTarget.contains(next)) return;

      if (markdownEditPreferred && editor) {
        const md = markdownDraft !== null ? markdownDraft : value;
        if (md.length <= COMMENT_CHARACTER_LIMIT) {
          editor.commands.setContent(markdownToHtml(md), false);
        }
      }

      setMarkdownDraft(null);
      setIsFocused(false);
    },
    [markdownEditPreferred, editor, markdownDraft, value]
  );

  const handleMarkdownTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMarkdownDraft(e.target.value);
      if (e.target.value.length <= COMMENT_CHARACTER_LIMIT) {
        lastEmittedValueRef.current = e.target.value;
        onChange(e.target.value);
      }
      const ta = e.target;
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(Math.max(ta.scrollHeight, 148), 300)}px`;
    },
    [onChange]
  );

  const applyMarkdownSourceChange = useCallback(
    (next: string, selectionStart: number, selectionEnd: number) => {
      const ta = markdownTextareaRef.current;
      if (ta) {
        ta.value = next;
        ta.focus();
        ta.setSelectionRange(selectionStart, selectionEnd);
      }
      setMarkdownDraft(next);
      if (next.length <= COMMENT_CHARACTER_LIMIT) {
        lastEmittedValueRef.current = next;
        onChange(next);
      }
    },
    [onChange]
  );

  const markdownSourceContext = useMemo(
    () => ({
      getValue: () => markdownDraft ?? value,
      getSelection: () => {
        const ta = markdownTextareaRef.current;
        return { start: ta?.selectionStart ?? 0, end: ta?.selectionEnd ?? 0 };
      },
      apply: applyMarkdownSourceChange
    }),
    [markdownDraft, value, applyMarkdownSourceChange]
  );

  const handleMarkdownToggle = useCallback(() => {
    if (!editor) return;
    if (markdownEditPreferred) {
      const md = markdownDraft !== null ? markdownDraft : htmlToMarkdown(editor.getHTML());
      if (md.length <= COMMENT_CHARACTER_LIMIT) {
        lastEmittedValueRef.current = md;
        onChange(md);
        editor.commands.setContent(markdownToHtml(md), false);
      }
      setMarkdownEditPreferred(false);
      setMarkdownDraft(null);
    } else {
      const md = htmlToMarkdown(editor.getHTML());
      setMarkdownDraft(md);
      setMarkdownEditPreferred(true);
    }
  }, [editor, markdownEditPreferred, markdownDraft, onChange]);

  useEffect((): (() => void) | void => {
    if (!editor) return;
    onEditorEvent(editor, TOGGLE_MARKDOWN_SOURCE_EVENT, handleMarkdownToggle);
    return (): void => offEditorEvent(editor, TOGGLE_MARKDOWN_SOURCE_EVENT, handleMarkdownToggle);
  }, [editor, handleMarkdownToggle]);

  useLayoutEffect(() => {
    if (!editor) return;
    if (showMarkdownTextarea && !prevShowMarkdownTextareaRef.current) {
      const ta = markdownTextareaRef.current;
      ta?.focus();
      if (ta) {
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(Math.max(ta.scrollHeight, 148), 300)}px`;
      }
    } else if (!showMarkdownTextarea && prevShowMarkdownTextareaRef.current && isFocused) {
      editor.chain().focus().run();
    }
    prevShowMarkdownTextareaRef.current = showMarkdownTextarea;
  }, [showMarkdownTextarea, editor, isFocused]);

  useLayoutEffect(() => {
    const el = editorBoxRef.current;
    if (!el) return;
    const update = () => setComposerWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [editor]);

  const providerValue = useMemo(() => ({ editor }), [editor]);

  const resolvedMentionUiContext = useMemo(
    () =>
      resolveMentionPreviewContext({
        ...(mentionPreviewContext ?? {}),
        descriptionMarkdown: value
      }),
    [mentionPreviewContext, value]
  );

  const composerDensity = commentComposerToolbarDensity(composerWidth);
  const toolbarPanelCompact = composerDensity != null;
  const toolbarSidebarTightViewport = composerDensity === 'tight';
  const toolbarSidebarFillWidth = composerDensity === 'compact';
  const toolbarNarrowViewportScroll = false;

  if (!editor) return null;

  const charCount = showMarkdownTextarea ? (markdownDraft ?? value).length : htmlToMarkdown(editor.getHTML()).length;

  const markdownTextarea = showMarkdownTextarea ? (
    <textarea
      ref={markdownTextareaRef}
      className={`${styles.editor} ${styles.markdownTextarea}`}
      value={markdownDraft !== null ? markdownDraft : value}
      onChange={handleMarkdownTextareaChange}
      onKeyDown={e => {
        if (isMarkdownToggleShortcut(e)) {
          e.preventDefault();
          handleMarkdownToggle();
          return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && submitTask) {
          e.preventDefault();
          submitTask();
        }
      }}
      placeholder={placeholder}
      aria-label={placeholder}
      data-cy={`${editorDataCy}-markdown`}
    />
  ) : null;

  return (
    <EditorContext.Provider value={providerValue}>
      <MentionPreviewContext.Provider value={resolvedMentionUiContext}>
        <div
          ref={popupContainerRef}
          className={styles.container}
          style={{ position: 'relative' }}
          onFocus={handleContainerFocus}
          onBlur={handleContainerBlur}
        >
          <div
            ref={editorBoxRef}
            className={`${styles.editorBox} ${isFocused ? styles.editorBoxFocused : ''} ${
              isPrivate ? styles.editorBoxPrivate : ''
            } ${collapseVisual ? styles.editorBoxCollapsing : ''}`}
          >
            <div className={`${styles.editorWrapper} ${isFocused ? '' : styles.editorWrapperUnfocused}`}>
              <EditorContent editor={editor} className={showMarkdownTextarea ? styles.editorHidden : undefined} />
              {markdownTextarea}
            </div>
            <div
              className={styles.toolbarDock}
              aria-hidden={!isFocused}
              onMouseDown={e => {
                if ((e.target as HTMLElement).closest('input, textarea')) return;
                e.preventDefault();
              }}
            >
              <Toolbar
                variant="bottom"
                panelCompact={toolbarPanelCompact}
                sidebarTightViewport={toolbarSidebarTightViewport}
                sidebarFillToolbarWidth={toolbarSidebarFillWidth}
                narrowViewportToolbarScroll={toolbarNarrowViewportScroll}
                markdownEditPreferred={markdownEditPreferred}
                markdownSourceModeActive={showMarkdownTextarea}
                markdownSource={showMarkdownTextarea ? markdownSourceContext : null}
                onMarkdownToggle={handleMarkdownToggle}
              />
            </div>
          </div>
          <div className={styles.footer}>
            {isFocused && (
              <span className={styles.count}>
                {charCount} / {COMMENT_CHARACTER_LIMIT}
              </span>
            )}
            {isFocused && submitTask && (
              <span className={styles.submitHint}>
                {navigator.platform.indexOf('Mac') > -1 ? 'Cmd' : 'Ctrl'} + Enter to submit
              </span>
            )}
          </div>
          <RichTextBubbleMenu
            editor={editor}
            showMarkdownTextareaRef={showMarkdownTextareaRef}
            appendToRef={popupContainerRef}
          />
        </div>
      </MentionPreviewContext.Provider>
    </EditorContext.Provider>
  );
};

export default RichTextComment;
